import { useContext } from 'react';
import { PageActionsContext } from './pageActionsContext.js';
import styles from './settingsForm.module.css';

/**
 * "Remove page" button rendered in a settings panel header, beside the
 * module's "Reset settings". No-ops (renders nothing) outside a
 * PageActionsContext provider. Fronts a window.confirm so a stray tap on
 * a board doesn't drop a page mid-prep.
 */
export function RemovePageButton() {
  const ctx = useContext(PageActionsContext);
  if (!ctx?.onRemove) return null;
  const { pageLabel, onRemove } = ctx;
  const handle = () => {
    const ok = window.confirm(
      `Remove the ${pageLabel} page from this classroom?\n\n` +
        `It won't appear in morning meeting until you add it back from Settings. Any saved ` +
        `settings for the page are kept.`,
    );
    if (ok) onRemove();
  };
  return (
    <button
      type="button"
      className={styles.removeButton}
      onClick={handle}
      data-testid={`remove-page-${pageLabel.toLowerCase().replace(/\s+/g, '-')}`}
    >
      Remove page
    </button>
  );
}

/**
 * Enable/disable button for the permanent classroom pages (YouTube, Links),
 * rendered in the panel header beside "Reset settings". A disabled page is
 * greyed in the Classroom Pages strip and hidden in the live nav; it stays
 * configurable here. No-ops outside a provider that supplies `toggle`.
 */
export function PageEnableToggleButton() {
  const ctx = useContext(PageActionsContext);
  if (!ctx?.toggle) return null;
  const { pageLabel, toggle } = ctx;
  const slug = pageLabel.toLowerCase().replace(/\s+/g, '-');
  return (
    <button
      type="button"
      role="switch"
      aria-checked={toggle.enabled}
      className={
        toggle.enabled
          ? `${styles.pageEnableButton} ${styles.pageEnableButtonOn}`
          : styles.pageEnableButton
      }
      onClick={() => toggle.onToggle(!toggle.enabled)}
      data-testid={`toggle-page-${slug}`}
    >
      {toggle.enabled ? 'Enabled · shown in class' : 'Disabled · hidden in class'}
    </button>
  );
}
